/**
 * Customer Type Action Handlers
 *
 * Handles customer type update operations for both single and bulk operations.
 * Provides consistent behavior across table and drawer contexts.
 */

import { useQueryClient } from '@tanstack/react-query';

import { useUpdateCustomerType } from '@/hooks/useCustomerType';
import { CustomerData } from '@/sections/customerManagement/types';
import { CustomerTypeValue } from '@/types';

/**
 * Hook for customer type update operations
 *
 * Supports both single and bulk customer type updates with
 * proper error handling and cache invalidation.
 *
 * @returns Object containing handleUpdateCustomerType function
 *
 * @example
 * ```tsx
 * const { handleUpdateCustomerType } = useCustomerTypeHandlers();
 *
 * // Single customer update
 * await handleUpdateCustomerType([customer], 'SAAS');
 *
 * // Bulk customer update
 * await handleUpdateCustomerType(selectedCustomers, 'MANAGED');
 * ```
 */
export const useCustomerTypeHandlers = () => {
  const updateCustomerType = useUpdateCustomerType();
  const queryClient = useQueryClient();

  /**
   * Updates customer type for one or more customers
   *
   * Executes updates in parallel using Promise.allSettled to handle
   * partial failures gracefully. Invalidates relevant queries after
   * successful updates.
   *
   * @param customers - Array of customers to update (single or multiple)
   * @param newType - New customer type to apply
   * @throws Error if all operations fail
   */
  const handleUpdateCustomerType = async (
    customers: CustomerData[],
    newType: CustomerTypeValue
  ) => {
    // Execute all updates in parallel
    const promises = customers.map(customer =>
      updateCustomerType.mutateAsync({
        email: customer.email,
        customerType: newType,
      })
    );

    // Use Promise.allSettled to handle partial failures
    const results = await Promise.allSettled(promises);

    // Count successes and failures
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    // Invalidate relevant queries if any operations succeeded
    if (succeeded > 0) {
      queryClient.invalidateQueries({
        queryKey: ['my', { resource: 'account' }],
      });
    }

    // Log failures for debugging
    if (failed > 0) {
      const failedResults = results
        .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
        .map((r, i) => ({
          customer: customers[i]?.email,
          error: r.reason,
        }));

      console.error(
        '[useCustomerTypeHandlers] Failed to update customer type:',
        failedResults
      );
    }

    // Throw error if all operations failed
    if (failed === customers.length) {
      throw new Error('Failed to update customer type for all customers');
    }

    // Return success/failure counts for caller to handle messaging
    return { succeeded, failed };
  };

  return {
    handleUpdateCustomerType,
    isPending: updateCustomerType.isPending,
  };
};
